import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText } from 'lucide-react';

const SectionText = ({ children }) => <p className="text-[#A1A1AA] leading-relaxed mb-2">{children}</p>;
const SectionList = ({ items }) => (
  <ul className="list-disc list-inside text-[#A1A1AA] space-y-2 ml-4">
    {items.map((item) => <li key={item}>{item}</li>)}
  </ul>
);

const SECTIONS = [
  {
    title: "1. Acceptance of Terms",
    content: <SectionText>By accessing and using "I May Help You" (the "Service"), you agree to be bound by these Terms of Service. This service is rated <strong className="text-[#F9FAFB]">13+</strong> and requires users to be at least 13 years of age. By creating an account, you confirm that you meet this age requirement. If you do not agree to these terms, please do not use the Service.</SectionText>,
  },
  {
    title: "2. Service Description",
    content: (
      <>
        <p className="text-[#A1A1AA] leading-relaxed mb-2">
          <strong className="text-[#EF4444]">IMPORTANT:</strong> "I May Help You" provides AI-powered emotional support and conversation services. This is NOT therapy. This is NOT medical advice. This is NOT a substitute for professional mental health care. No licensed therapist, psychologist, psychiatrist, or medical professional is involved in the delivery of this service.
        </p>
        <SectionText>The AI is designed to listen, reflect, and ask questions. It does not diagnose, prescribe, or treat any condition. If you require professional help, please contact a licensed mental health provider.</SectionText>
      </>
    ),
  },
  {
    title: "3. User Responsibilities",
    content: (
      <>
        <SectionText>You agree to:</SectionText>
        <SectionList items={[
          'Provide accurate and truthful information',
          'Maintain the confidentiality of your account',
          'Not use the Service for illegal purposes',
          'Not share content containing hate speech, violence, or harmful ideology',
          'Not attempt to bypass rate limits or abuse the Service',
          'Seek professional help for serious mental health concerns',
        ]} />
      </>
    ),
  },
  {
    title: "4. Account Security",
    content: (
      <>
        <SectionText>You are responsible for maintaining the security of your account. We implement security measures including:</SectionText>
        <SectionList items={[
          'Password requirements (minimum 8 characters, 1 letter, 1 number, 1 special character)',
          'Rate limiting (10 attempts per minute)',
          'Account lockout after 5 failed login attempts (15-minute cooldown)',
        ]} />
      </>
    ),
  },
  {
    title: "5. Subscription and Payments",
    content: (
      <>
        <SectionText>Premium Access:</SectionText>
        <SectionList items={[
          'Cost: $1 USD for 24 hours of unlimited access and Adaptive AI mode',
          'Free tier: 3 responses per 24 hours',
          'Payments processed securely through PayPal',
          'No automatic renewal - each purchase is for 24 hours only',
          'No refunds after AI response is generated',
        ]} />
      </>
    ),
  },
  {
    title: "6. Content Moderation",
    content: (
      <>
        <SectionText>All user submissions are analyzed for harmful content. We use AI-powered moderation to detect:</SectionText>
        <SectionList items={['Hate speech', 'Violence or threats', 'Harmful ideology']} />
        <p className="text-[#A1A1AA] leading-relaxed mt-3">
          <strong className="text-[#EF4444]">High severity violations result in immediate and permanent account termination without refund.</strong>
        </p>
      </>
    ),
  },
  {
    title: "7. Intellectual Property",
    content: <SectionText>The Service, including all content, features, and functionality, is owned by "I May Help You" and protected by copyright and trademark laws. AI-generated responses are provided as-is for your personal use only.</SectionText>,
  },
  {
    title: "8. Disclaimer of Warranties",
    content: (
      <>
        <SectionText>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. We do not guarantee:</SectionText>
        <SectionList items={[
          'Accuracy or reliability of AI responses',
          'Uninterrupted or error-free service',
          'That the Service will meet your specific needs',
          'Medical or therapeutic outcomes',
        ]} />
      </>
    ),
  },
  {
    title: "9. Limitation of Liability",
    content: <SectionText>We shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service. Our total liability shall not exceed the amount you paid for the Service in the past 30 days.</SectionText>,
  },
  {
    title: "10. Mental Health Crisis",
    content: (
      <>
        <p className="text-[#A1A1AA] leading-relaxed mb-2">
          <strong className="text-[#EF4444]">EMERGENCY NOTICE:</strong> If you are experiencing a mental health crisis or having thoughts of self-harm, please:
        </p>
        <SectionList items={[
          'Call 988 (Suicide & Crisis Lifeline) in the US',
          'Call your local emergency services',
          'Visit your nearest emergency room',
          'Contact a mental health professional immediately',
        ]} />
      </>
    ),
  },
  {
    title: "11. Donations",
    content: <SectionText>Donations are split 50/50 between the developer (for app maintenance) and Mental Health Canada (registered charity). Donations are non-refundable.</SectionText>,
  },
  {
    title: "12. Termination",
    content: (
      <>
        <SectionText>We reserve the right to suspend or terminate your account at any time for:</SectionText>
        <SectionList items={[
          'Violation of these Terms',
          'Content moderation violations',
          'Fraudulent activity',
          'Any conduct we deem harmful to the Service or other users',
        ]} />
      </>
    ),
  },
  {
    title: "13. Data Retention & Safety",
    content: (
      <>
        <SectionText>For the safety of our users and to comply with applicable law:</SectionText>
        <SectionList items={[
          'All conversations, messages, and AI responses are stored permanently',
          'Content moderation decisions are logged with timestamps',
          'Crisis intervention events are logged as safety records',
          'Account deletion removes your profile but conversation records are retained for safety and legal compliance',
          'Records may be disclosed to law enforcement if required by law or if we reasonably believe disclosure is necessary to protect the safety of any person',
          'By using this service, you consent to this data retention policy',
        ]} />
      </>
    ),
  },
  {
    title: "14. Changes to Terms",
    content: <SectionText>We may modify these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms.</SectionText>,
  },
  {
    title: "15. Contact Information",
    content: (
      <SectionText>
        For questions about these Terms, contact us at:{' '}
        <a href="mailto:Mhvk2793@gmail.com" className="text-[#8B5CF6] hover:text-[#9D6FFF]">Mhvk2793@gmail.com</a>
      </SectionText>
    ),
  },
  {
    title: "16. Governing Law",
    content: <SectionText>These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict of law principles.</SectionText>,
  },
];

const TermsPage = () => (
  <div className="min-h-screen bg-[#0A0A0A] py-12 px-4" data-testid="terms-page">
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <FileText className="w-16 h-16 text-[#8B5CF6] mx-auto mb-4" />
        <h1 className="text-4xl sm:text-5xl font-light tracking-tighter mb-4" style={{ fontFamily: 'Outfit' }}>
          Terms of <span className="text-[#8B5CF6]">Service</span>
        </h1>
        <p className="text-lg text-[#A1A1AA]">Last updated: January 2026</p>
      </div>
      <Card className="card-surface border-[#27272A]">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Outfit' }}>Agreement to Terms</CardTitle>
        </CardHeader>
        <CardContent className="prose prose-invert max-w-none space-y-6">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h3 className="text-xl font-semibold text-[#F9FAFB] mb-3" style={{ fontFamily: 'Outfit' }}>{section.title}</h3>
              {section.content}
            </section>
          ))}
        </CardContent>
      </Card>
    </div>
  </div>
);

export default TermsPage;
